import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { dropTask } from 'ember-concurrency';
import Button from 'tio-ui/components/button';
import Input from 'tio-ui/components/input';
import type { ErrorsSignature } from 'tio-ui/components/error-messages';
import CategorizedUpload from 'tio-common/components/tuition-assistance/forms/categorized-upload';
import UploadAttachments from 'tio-common/components/tuition-assistance/forms/upload-attachments';
import { collectApplicationAssets } from 'tio-employee/utils/tas/application-assets';
import { usesCategorizedUpload } from 'tio-employee/utils/tas/evidence-attachments';
import { getAllValuesForTasField } from 'tio-common/utils/tuition-assistance/fields';
import { inputsForFields } from 'tio-common/utils/data/inputs-for-fields';
import SubmitEvidenceCourseRow from './submit-evidence-course-row';
import SubmitEvidenceActions from './submit-evidence-actions';
import ChecklistRow from './checklist-row';
import { hasStep, isStepDone } from 'tio-employee/utils/tas/evidence-checklist';
import type { EvidenceChecklist } from 'tio-employee/utils/tas/evidence-checklist';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasCourseExtension as TasCourseModel } from 'tio-data/data/resources/tas-course.ext';
import type { ConditionExtension as ConditionModel } from 'tio-data/data/resources/condition.ext';
import type PartnerService from 'tio-employee/services/partner';

export interface SubmitEvidenceBundle {
  onSaveGrade: (course: TasCourseModel, grade: string) => Promise<void>;
  onSaveGpa: (gpa: number) => Promise<void>;
  onAcceptConditions: (conditions: ConditionModel[]) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  submitError: string | ErrorsSignature | null;
  canSubmit: boolean;
  /**
   * The requirements this card is asking for, built by ManagePage from the same
   * getters `canSubmit` reads. Drives the header count, which row markers show
   * satisfied, and the reason beside a disabled Submit.
   */
  checklist: EvidenceChecklist;
  /** True while the course-approval card is on screen owning the document
   * surface, so this card can stand its own uploader down. */
  approvalCardOwnsDocuments: boolean;
}

interface SubmitEvidenceBodySignature {
  Args: {
    application: TasApplicationModel;
    /** Optional so isolated component tests can render this task without
     * wiring the full ManagePage-owned bundle; every real render passes one. */
    evidence?: SubmitEvidenceBundle;
  };
  Element: HTMLDivElement;
}

/** Exported so ManagePage's submit gate reads the SAME classification this card
 * uploads under — see manage-page.gts's `hasRequiredEvidenceDocuments`. */
export const EVIDENCE_CLASSIFICATION = 'COMPLETION_DOCUMENTS';

/** Grade, GPA and upload checklist; shares the ManagePage evidence bundle with SubmitEvidenceActions. */
export default class SubmitEvidenceBody extends Component<SubmitEvidenceBodySignature> {
  @service declare partner: PartnerService;
  @tracked pendingGpa = '';

  get application() {
    return this.args.application;
  }

  get programTemplate() {
    return this.application.tasProgramInstance?.tasProgramTemplate;
  }

  get activeCourses() {
    return this.application.activeCourses ?? [];
  }

  get grades() {
    const exclusiveGradeOptions = (getAllValuesForTasField(
      'EXCLUSIVE_ACADEMIC_PERFORMANCE_OPTIONS',
      this.programTemplate?.fields
    ) ?? []) as string[];
    if (exclusiveGradeOptions.length > 0) {
      return exclusiveGradeOptions;
    }
    return inputsForFields['COURSE_GRADE'].options.map((option) => option.value);
  }

  // Share company capability and configured categories with the submit gate so it only requires rendered rows.
  get useCategorizedUpload() {
    return usesCategorizedUpload({
      isAiSummariesEnabled: this.partner.companySettings?.tuitionAssistance?.isAiSummariesEnabled,
      documentEntries: this.programTemplate?.documentEntriesFor(EVIDENCE_CLASSIFICATION) ?? [],
    });
  }

  /**
   * Visibility permits optional uploads; required gates submission. Required-but-hidden must still be satisfiable.
   * ATTEND also exposes uploads for legacy templates with neither flag.
   */
  get isDocumentSurfaceConfigured() {
    return Boolean(
      this.programTemplate?.attachmentsForCourseCompletionVisible ||
      this.application.isEvidenceRequired ||
      this.application.state === 'TAS.ApplicationState.ATTEND'
    );
  }

  /**
   * Withheld while the course-approval card holds the window, leaving one upload
   * surface on the screen. That card is the only one actionable there — evidence
   * is not submittable until ATTEND — and this card's list spans every asset on
   * the application, so a second dropzone here draws the same approval documents
   * while filing uploads under a classification the approver's rows never see.
   * ManagePage drops the matching checklist step in the same states, so nothing
   * is counted that the card gives no way to satisfy.
   */
  get showUploadSurface() {
    return this.isDocumentSurfaceConfigured && !this.args.evidence?.approvalCardOwnsDocuments;
  }

  /** Say where the documents went, rather than leaving a silent gap. */
  get isUploadDeferred() {
    return (
      this.isDocumentSurfaceConfigured && Boolean(this.args.evidence?.approvalCardOwnsDocuments)
    );
  }

  /** Application-wide assets for the upload history and free-form list, including earlier approval steps. */
  get allRelatedAssets() {
    return collectApplicationAssets(this.application);
  }

  get showGPAField() {
    const typeClassification = this.application.typeClassification;
    return Boolean(
      this.programTemplate?.calculateTotalByGPA &&
      (typeClassification === 'TAS.ProgramType.3' || typeClassification === 'TAS.ProgramType.4.3')
    );
  }

  /**
   * Absent-step defaults matter here: a template that shows the uploader
   * without requiring a document contributes no `documents` step, and the row
   * then draws as satisfied rather than nagging for work nothing enforces.
   */
  get isDocumentsDone(): boolean {
    return this.args.evidence ? isStepDone(this.args.evidence.checklist, 'documents') : false;
  }

  get isGpaDone(): boolean {
    return this.args.evidence ? isStepDone(this.args.evidence.checklist, 'gpa') : false;
  }

  /**
   * The uploader renders on `visible` but only counts on `required`, so a
   * template that sets the first and not the second gets a row the card's
   * total deliberately ignores. Say so on the row rather than letting it wear
   * a satisfied check it did not earn.
   */
  get isGradesOptional(): boolean {
    return this.args.evidence ? !hasStep(this.args.evidence.checklist, 'grades') : false;
  }

  get isDocumentsOptional(): boolean {
    return this.args.evidence ? !hasStep(this.args.evidence.checklist, 'documents') : false;
  }

  updateGpa = (value: string) => {
    this.pendingGpa = value;
  };

  saveGpa = dropTask(async () => {
    if (!this.pendingGpa.trim() || !this.args.evidence) {
      return;
    }
    await this.args.evidence.onSaveGpa(Number(this.pendingGpa));
  });

  <template>
    <div class="tio-col gap-4" ...attributes>
      {{#if this.activeCourses.length}}
        <div class="tio-col gap-2">
          {{#each this.activeCourses as |course|}}
            <SubmitEvidenceCourseRow
              @course={{course}}
              @grades={{this.grades}}
              @onSave={{@evidence.onSaveGrade}}
              @isOptional={{this.isGradesOptional}}
            />
          {{/each}}
        </div>
      {{/if}}

      {{#if this.showGPAField}}
        <ChecklistRow @title={{t "tas.application.grade_point_average"}} @isDone={{this.isGpaDone}}>
          <div class="tio-row items-center gap-2">
            <Input
              @size="lg"
              type="number"
              min="0"
              max="4.0"
              step="0.01"
              id="evidence-gpa"
              data-test-evidence-gpa-input
              @value={{this.pendingGpa}}
              class="w-40"
              @onChange={{this.updateGpa}}
            />
            <Button
              @intent="primary"
              @isDisabled={{not this.pendingGpa}}
              @isRunning={{this.saveGpa.isRunning}}
              data-test-evidence-gpa-cta
              {{on "click" this.saveGpa.perform}}
            >
              {{t "common.save"}}
            </Button>
          </div>
        </ChecklistRow>
      {{/if}}

      {{#if this.showUploadSurface}}
        <ChecklistRow
          @title={{t "tuition_assistance.applications.manage.tasks.checklist_documents_title"}}
          @isDone={{this.isDocumentsDone}}
          @isOptional={{this.isDocumentsOptional}}
          data-test-evidence-upload
        >
          {{#if this.useCategorizedUpload}}
            {{! @relatedAssets feeds the "Previously uploaded" history —
              earlier steps' documents, view-only — the legacy page renders
              from its own allAttachments union (#10393). }}
            <CategorizedUpload
              @programTemplate={{this.programTemplate}}
              @classification={{EVIDENCE_CLASSIFICATION}}
              @attachable={{this.application}}
              @attachableType="tasApplication"
              @existingAssets={{this.application.tasAssets}}
              @relatedAssets={{this.allRelatedAssets}}
              @compact={{true}}
            />
          {{else}}
            {{! The legacy page's free-form list also renders the full union,
              not just application-level assets (#10393). }}
            <UploadAttachments
              @programTemplate={{this.programTemplate}}
              @classification={{EVIDENCE_CLASSIFICATION}}
              @attachable={{this.application}}
              @attachableType="tasApplication"
              @useDropzone={{true}}
              @attachmentListItems={{this.allRelatedAssets}}
            />
          {{/if}}
        </ChecklistRow>
      {{else if this.isUploadDeferred}}
        <p class="tio-text-sm-normal tio-fg-secondary" data-test-evidence-upload-deferred>
          {{t "tuition_assistance.applications.manage.tasks.documents_after_approval"}}
        </p>
      {{/if}}

      {{! Terms and Submit close the checklist rather than sitting in the card's
        always-visible footer. Rendered outside, they were the only two controls
        a collapsed card showed — the end of the task with none of its work — so
        the card read as finished and nobody opened it. }}
      <SubmitEvidenceActions @application={{this.application}} @evidence={{@evidence}} />
    </div>
  </template>
}
